import { useQuery } from "@tanstack/react-query";
import { Copy, Link2, Share2, UserCircle, Users } from "lucide-react";

import { api } from "@/lib/api";
import { CommissionItem } from "@/components/commission-item";
import { Header } from "@/components/header";
import { Button, Card, Empty, ErrorView, Loading } from "@/components/kit";
import { useToast } from "@/components/toast";
import { date, eur } from "@/lib/format";

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    document.execCommand("copy");
    ta.remove();
  }
}

function CodeCard({ d, onCopy, onCopyLink, onShare }) {
  return (
    <div className="flex flex-col gap-3 rounded-[20px] border border-[#D4AF37] bg-[#292415] p-6">
      <p className="t-label !text-[#F3E5AB]">MON CODE DE PARRAINAGE</p>
      <button type="button" data-testid="referral-copy-code" onClick={onCopy} className="focus-ring flex w-fit items-center gap-3 rounded-lg" aria-label="Copier le code">
        <span data-testid="referral-code" className="t-display tracking-[2px] !text-[#D4AF37]">{d.code}</span>
        <Copy className="h-[22px] w-[22px] text-[#D4AF37]" />
      </button>
      <p className="text-sm text-white">
        Invitez d'autres agents : à chaque contrat signé grâce à un filleul, vous recevez {eur(d.bonus_amount)} (après validation du gérant).
      </p>
      <button type="button" data-testid="referral-link-box" onClick={onCopyLink} className="focus-ring flex items-center gap-2 rounded-xl bg-[#2A2A2A] p-3 text-left hover:bg-[#333333]">
        <Link2 className="h-[18px] w-[18px] shrink-0 text-[#D4AF37]" />
        <span data-testid="referral-link" className="t-body flex-1 truncate">{d.link}</span>
      </button>
      <p className="t-caption">Ce lien ouvre l'inscription avec votre code déjà rempli.</p>
      <div className="flex gap-2">
        <Button testID="referral-copy-button" title="Copier le lien" icon={Copy} variant="secondary" className="flex-1" onClick={onCopyLink} />
        <Button testID="referral-share-button" title="Partager" icon={Share2} className="flex-1" onClick={onShare} />
      </div>
    </div>
  );
}

export default function Referral() {
  const toast = useToast();
  const q = useQuery({ queryKey: ["referral"], queryFn: () => api("/agent/referral") });
  const d = q.data;
  const copy = async () => {
    await copyText(d.code);
    toast("Code copié !");
  };
  const copyLink = async () => {
    await copyText(d.link);
    toast("Lien copié !");
  };
  const share = async () => {
    const text = `Rejoignez Ma Maison Suivie, la plateforme nationale de recommandations immobilières. Inscrivez-vous avec mon lien de parrainage (code ${d.code} déjà rempli) : ${d.link}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Ma Maison Suivie", text, url: d.link });
      } catch {}
    } else {
      await copyText(text);
      toast("Message d'invitation copié !");
    }
  };
  return (
    <div data-testid="referral-screen" className="flex flex-col">
      <Header title="Parrainage" />
      {q.isLoading ? <Loading /> : q.isError || !d ? <ErrorView message="Erreur de chargement." onRetry={() => q.refetch()} /> : (
        <div className="mx-auto grid w-full max-w-6xl gap-6 p-4 pb-8 md:p-8 lg:grid-cols-2">
          <div className="flex flex-col gap-4">
            <CodeCard d={d} onCopy={copy} onCopyLink={copyLink} onShare={share} />
            <div className="flex gap-3">
              <Card className="flex-1"><p className="t-caption">Filleuls</p><p data-testid="referral-count" className="t-number">{d.count}</p></Card>
              <Card className="flex-1"><p className="t-caption">Récompenses gagnées</p><p data-testid="referral-earned" className="t-number !text-[#27AE60]">{eur(d.totals.earned)}</p></Card>
            </div>
            <p data-testid="referral-pending" className="t-caption">En attente de validation : {eur(d.totals.pending)} · Payé : {eur(d.totals.paid)}</p>
          </div>
          <div className="flex flex-col gap-3">
            <h2 className="t-h2 font-display !font-normal">Mes filleuls</h2>
            {d.filleuls.length === 0 ? (
              <Empty icon={Users} title="Aucun filleul pour l'instant" text="Partagez votre code avec vos confrères." />
            ) : (
              d.filleuls.map((f) => (
                <div key={f.id} data-testid={`filleul-${f.id}`} className="flex items-center gap-3 rounded-xl bg-[#1E1E1E] p-3">
                  <UserCircle className="h-7 w-7 text-[#C0C0C0]" aria-hidden />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white">{f.first_name} {f.last_name}</p>
                    <p className="t-caption">{f.agency ? `${f.agency} · ` : ""}Inscrit le {date(f.created_at)}</p>
                  </div>
                  <span className="t-caption !text-[#27AE60]">{f.signed} contrat(s)</span>
                </div>
              ))
            )}
            {d.rewards.length > 0 && <h2 className="t-h2 mt-2 font-display !font-normal">Récompenses</h2>}
            {d.rewards.map((r) => <CommissionItem key={r.id} c={r} />)}
          </div>
        </div>
      )}
    </div>
  );
}
